import React, { useState, useEffect, useRef } from "react";
import { IonPage, IonContent, IonButton, IonText, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const CountingNumbers: React.FC = () => {
    const emojis = ["🍎", "🐶", "🚗", "🌟", "🦋", "🍕", "🎈", "🐠", "🍉", "🚀"];
    const [objects, setObjects] = useState<string[]>([]);
    const [correctCount, setCorrectCount] = useState<number>(0);
    const [options, setOptions] = useState<number[]>([]);
    const [message, setMessage] = useState<string>("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateNewQuestion();
        document.title = "Counting Numbers Worksheet for LKG | Early Math Practice | Mama’s Guidance";
    }, []);

    const generateNewQuestion = () => {
        const randomEmoji = emojis[Math.floor(Math.random() * emojis.length)];
        const count = Math.floor(Math.random() * 10) + 11; // Random count between 11-20
        const newObjects = Array(count).fill(randomEmoji);
        setObjects(newObjects);
        setCorrectCount(count);

        // Generate options
        const wrongOption1 = count + (Math.random() > 0.5 ? 1 : -1);
        const wrongOption2 = count + (Math.random() > 0.5 ? 2 : -2);
        const optionsArray = [count, wrongOption1, wrongOption2].sort(() => Math.random() - 0.5);
        setOptions(optionsArray);
        setMessage("");
    };

    const checkAnswer = (selected: number) => {
        if (selected === correctCount) {
            setShowImage(true);
            setTimeout(() => {
                generateNewQuestion()
                setShowImage(false);
            }, 5000)
        } else {
            setShowImage(false);
            setMessage("❌ Oops! Try again.");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Explore fun and interactive counting and number recognition activities for LKG students on Mama’s Guidance—designed to build strong foundational math skills through engaging visuals, games, and worksheets.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord pickColor matchImage match-container" style={{ textAlign: "center", padding: "20px" }}>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Counting Numbers</h1>

                                    <p>
                                        Counting is one of the first and most important math skills a child learns. At the LKG level, children are introduced to numbers from 1 to 50 using <strong>real-life objects, fun visuals, and hands-on activities</strong>. This worksheet helps young learners practice counting through engaging and simple exercises.
                                    </p>

                                    <h2>🔢 What Is Counting?</h2>
                                    <p>
                                        Counting means saying numbers in the correct order while keeping track of objects. It&apos;s the foundation for all future math skills, such as <strong>addition, subtraction, and number comparison</strong>. Children first learn to <strong>count forward</strong> using fingers, toys, or pictures.
                                    </p>

                                    <h2>🎯 What Will Your Child Learn?</h2>
                                    <ul>
                                        <li>Recognizing numbers from 1 to 50</li>
                                        <li>Counting <strong>real or illustrated objects</strong> correctly</li>
                                        <li>Understanding the value each number represents</li>
                                        <li>Building <strong>concentration and number sense</strong></li>
                                    </ul>

                                    <h2>🖍️ Worksheet Highlights</h2>
                                    <ul>
                                        <li>Count and circle the correct number of objects</li>
                                        <li>Trace and write numbers from 1 to 50</li>
                                        <li>Match numbers to groups of objects</li>
                                        <li>Identify missing numbers in sequences</li>
                                    </ul>

                                    <h2>👩‍🏫 Teaching Tips for Parents</h2>
                                    <ul>
                                        <li>Use blocks, spoons, buttons, or toys for real counting practice</li>
                                        <li>Count out loud together during walks, playtime, or snacks</li>
                                        <li>Use rhymes and songs to make counting fun and memorable</li>
                                        <li>Make mistakes okay — praise effort and progress</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/lkg/mathematics/missing-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Missing Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/before-after-between-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Before / After / Between Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/skip-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Skip Number by 2 or 5</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🌟 Mama’s Guidance Tip</h2>
                                    <p>
                                        Place toys or snacks in front of your child and ask them to count aloud. For example, <strong>“Can you give me 5 grapes?”</strong> This makes counting part of everyday learning!
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Counting Numbers</h1>

                                <p>
                                    Counting is one of the first and most important math skills a child learns. At the LKG level, children are introduced to numbers from 1 to 50 using <strong>real-life objects, fun visuals, and hands-on activities</strong>. This worksheet helps young learners practice counting through engaging and simple exercises.
                                </p>

                                <h2>🔢 What Is Counting?</h2>
                                <p>
                                    Counting means saying numbers in the correct order while keeping track of objects. It&apos;s the foundation for all future math skills, such as <strong>addition, subtraction, and number comparison</strong>. Children first learn to <strong>count forward</strong> using fingers, toys, or pictures.
                                </p>

                                <h2>🎯 What Will Your Child Learn?</h2>
                                <ul>
                                    <li>Recognizing numbers from 1 to 50</li>
                                    <li>Counting real or illustrated objects correctly</li>
                                    <li>Understanding the value each number represents</li>
                                    <li>Building concentration and number sense</li>
                                </ul>

                                <h2>🖍️ Worksheet Highlights</h2>
                                <ul>
                                    <li>Count and circle the correct number of objects</li>
                                    <li>Trace and write numbers from 1 to 50</li>
                                    <li>Match numbers to groups of objects</li>
                                    <li>Identify missing numbers in sequences</li>
                                </ul>

                                <h2>👩‍🏫 Teaching Tips for Parents</h2>
                                <ul>
                                    <li>Use blocks, spoons, buttons, or toys for real counting practice</li>
                                    <li>Count out loud together during walks, playtime, or snacks</li>
                                    <li>Use rhymes and songs to make counting fun and memorable</li>
                                    <li>Make mistakes okay — praise effort and progress</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/long-short"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Long - Short</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/big-small"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Big - Small</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/full-empty"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Full - Empty</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Place toys or snacks in front of your child and ask them to count aloud. For example, <strong>“Can you give me 5 grapes?”</strong> This makes counting part of everyday learning!
                                </p>
                            </article>
                            <IonText>
                                <h2>How many objects do you see?</h2>
                            </IonText>
                            <IonText>
                                <h1 className="countingObjectHeading">{objects.join(" ")}</h1>
                            </IonText>
                            <div className="mt-4 mb-5" style={{ display: 'flex', gap: '30px' }}>
                                {options.map((option, index) => (
                                    <div className="countingObjectHeading background-blue" style={{ width: 'auto', padding: '15px' }} key={index} onClick={() => checkAnswer(option)}>
                                        {option}
                                    </div>
                                ))}
                            </div>
                            {message && <IonText><h3>{message}</h3></IonText>}
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default CountingNumbers;
